import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { formatDistanceToNow, parseISO } from "date-fns";
import {
  CheckCircle2, Circle, ArrowRight, Users, Eye, Heart, Bookmark, Activity, BarChart3, X,
} from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/common/Loading";
import { StatCard } from "@/components/dashboard/StatCard";
import { TeamInvitesBanner } from "@/components/dashboard/TeamInvitesBanner";
import { RecoveryEmailNudge } from "@/components/dashboard/RecoveryEmailNudge";
import { NotificationText } from "@/components/notifications/NotificationText";
import { useAuth } from "@/context/AuthContext";
import { tierLabel } from "@/lib/tiers";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: completion, isLoading } = useQuery({
    queryKey: ["profile-completion"],
    queryFn: async () => (await api.get("/account/profile-completion")).data,
  });
  const { data: stats } = useQuery({
    queryKey: ["analytics-overview"],
    queryFn: async () => (await api.get("/analytics/overview")).data,
  });
  const { data: notif } = useQuery({
    queryKey: ["dashboard-activity"],
    queryFn: async () => (await api.get("/notifications", { params: { limit: 8 } })).data,
  });
  const name = user?.preferred_first_name || user?.first_name || "there";
  const recent = notif?.notifications || [];
  const hideKey = `so_hide_profile_card_${user?.user_id || ""}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(`so_hide_profile_card_${user?.user_id || ""}`) === "1");
  const dismissCard = () => { localStorage.setItem(hideKey, "1"); setDismissed(true); };
  const showCompletion = !dismissed && (completion?.percent ?? 0) < 100;

  return (
    <div>
      <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">Welcome back, {name} 👋</h1>
      <p className="mt-1 text-muted-foreground">Here&apos;s your Startup Otter at a glance.</p>

      <TeamInvitesBanner />
      <RecoveryEmailNudge />

      {/* Stats strip */}
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Eye} label="Profile views (30d)" value={stats?.views?.total_30d ?? "—"} sub="Across your profiles" to="/dashboard/analytics" testId="dashboard-stat-views" />
        <StatCard icon={Users} label="Connections" value={stats?.network?.connections ?? "—"} sub={stats?.network?.pending_incoming ? `${stats.network.pending_incoming} pending` : "Grow your network"} to="/dashboard/network" testId="dashboard-stat-connections" />
        <StatCard icon={Heart} label="Fans" value={stats?.audience?.fans ?? "—"} sub="Followers of your profiles" to="/dashboard/network?tab=fans" testId="dashboard-stat-fans" />
        <StatCard icon={Bookmark} label="Saves received" value={stats?.audience?.bookmarks_received ?? "—"} sub="Bookmarks by others" to="/dashboard/bookmarks" testId="dashboard-stat-saves" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          {showCompletion && (
          <Card className="border-0 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-[640] text-foreground">Finish your profile</h2>
              <div className="flex items-center gap-3">
                {!isLoading && <span className="text-sm font-[600] text-primary" data-testid="dashboard-profile-completion-meter">{completion?.percent ?? 0}%</span>}
                <button onClick={dismissCard} aria-label="Dismiss" title="Dismiss" data-testid="dashboard-dismiss-profile-card" className="text-muted-foreground transition-colors hover:text-foreground"><X className="h-4 w-4" /></button>
              </div>
            </div>
            {isLoading ? <Loading /> : (
              <>
                <Progress value={completion?.percent ?? 0} className="mt-3 h-2" />
                <p className="mt-2 text-sm text-muted-foreground">{completion?.completed}/{completion?.total} steps complete — a complete profile gets more connections.</p>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(completion?.items || []).map((it) => (
                    <div key={it.key} className="flex items-center gap-2 text-sm">
                      {it.done ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                      <span className={it.done ? "text-muted-foreground line-through" : "text-foreground"}>{it.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex gap-2">
                  <Link to="/dashboard/profile"><Button data-testid="dashboard-edit-profile-button">Edit my profile <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
                  {!user?.account_complete && <Link to="/dashboard/account"><Button variant="secondary">Account settings</Button></Link>}
                </div>
              </>
            )}
          </Card>
          )}

          <Card className="border-0 p-6" data-testid="dashboard-activity-feed">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-[640] text-foreground">Recent activity</h2>
              <Link to="/dashboard/activity" className="text-sm font-[560] text-primary hover:underline" data-testid="dashboard-view-activity">View all</Link>
            </div>
            {recent.length === 0 ? (
              <div className="mt-4 flex flex-col items-center py-10 text-center">
                <Activity className="h-7 w-7 text-muted-foreground" />
                <p className="mt-2 text-sm text-muted-foreground">No activity yet. Connect, follow and post to see updates here.</p>
              </div>
            ) : (
              <ul className="mt-4 space-y-3.5">
                {recent.map((n) => (
                  <li
                    key={n.id}
                    onClick={() => { if (n.link) navigate(n.link); }}
                    className={`flex items-start gap-2.5 rounded-md p-1 -mx-1 transition-colors ${n.link ? "cursor-pointer hover:bg-secondary/50" : ""}`}
                    data-testid={`dashboard-activity-${n.id}`}
                  >
                    <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${n.read ? "bg-border" : "bg-primary"}`} />
                    <div className="min-w-0">
                      <p className="text-sm leading-snug text-foreground">
                        <NotificationText n={n} />
                      </p>
                      <p className="text-xs text-muted-foreground">{formatDistanceToNow(parseISO(n.created_at), { addSuffix: true })}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-6 lg:col-span-4">
          <Card className="border-0 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-[640] text-foreground">Your account</h2>
              <Link to="/dashboard/analytics" className="text-muted-foreground hover:text-primary" data-testid="dashboard-analytics-link"><BarChart3 className="h-4 w-4" /></Link>
            </div>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Member ID</dt><dd className="font-mono font-[560] text-foreground">{user?.user_id}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Plan</dt><dd className="font-[560] text-foreground">{tierLabel(user?.tier)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Email</dt><dd className="truncate font-[500] text-foreground">{user?.email}</dd></div>
            </dl>
            <div className="mt-5 space-y-2">
              <Link to="/dashboard/plans" className="block"><Button variant="secondary" className="w-full" data-testid="dashboard-view-plans">View plans</Button></Link>
              <Link to="/dashboard/billing" className="block"><Button variant="ghost" className="w-full" data-testid="dashboard-manage-billing">Manage billing</Button></Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
